import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Form, Input, message, Typography, Spin } from "antd";
import { MailOutlined, LockOutlined, WarningOutlined } from "@ant-design/icons";
import "antd/dist/antd.min.css";
import "./Login.css";
import {
  login,
  role,
  resetPassword,
  finalizeResetPassword,
} from "../Application/Auth.logic";
import CustomError from "../../Utils/Domain/CustomError";
import Storage from "../../Data/Domain/Storage";
import User from "../../Users/Domain/User";
import MFA from "../../MFA/UI/MFA";
import { verifyMFA, resendMFA } from "../../MFA/Infrastructure/MFA.logic";
import RestorePassword from "../../RestorePassword/UI/RestorePassword";

const { Link } = Typography;

const Login = ({ logout }) => {
  const [error, setError] = useState("");
  const [currentForm, setCurrentForm] = useState("login");
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);
  const [timer, setTimer] = useState(45);
  const [canResend, setCanResend] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    if (logout) localStorage.clear();
  }, [logout]);

  useEffect(() => {
    if (currentForm === "mfa" && timer > 0) {
      const countdown = setTimeout(() => setTimer((prev) => prev - 1), 1000);
      return () => clearTimeout(countdown);
    } else if (timer === 0) {
      setCanResend(true);
    }
  }, [timer, currentForm]);

  const clearErrorsAndSetLoading = () => {
    setError("");
    setLoading(true);
  };

  async function handleSubmit(values) {
    const { client_id, password } = values;
    setEmail(client_id);
    clearErrorsAndSetLoading();
    try {
      const response = await login({ client_id, password });
      if (response?.status === 230) {
        message.info("Se requiere verificación MFA");
        new Storage("mfa_session").setItem(response?.data?.session || "");
        new Storage("mfa_email").setItem(client_id);
        setCurrentForm("mfa");
        setTimer(45);
        setCanResend(false);
        return;
      }
      const role_object = await role();
      const first_role = User.getFirstRole(role_object.role);
      new Storage("rol").setItem(first_role);
      message.success("Bienvenido");
      navigate("/", { replace: true });
      if (typeof window !== "undefined") window.location.href = "/";
    } catch (error) {
      const messageError =
        error instanceof CustomError ? error.message : "General Error";
      setError(messageError);
    } finally {
      setLoading(false);
    }
  }

  const handleVerify = async (values) => {
    clearErrorsAndSetLoading();
    try {
      const { mfa_code } = values;
      await verifyMFA({ code: mfa_code });
      message.success("Verificación exitosa. Bienvenido");
      const role_object = await role();
      const first_role = User.getFirstRole(role_object.role);
      new Storage("rol").setItem(first_role);
      navigate("/", { replace: true });
      if (typeof window !== "undefined") window.location.href = "/";
    } catch (error) {
      setError(
        error instanceof CustomError ? error.message : "Código inválido"
      );
    } finally {
      setLoading(false);
    }
  };

  const handleResend = async () => {
    try {
      await resendMFA();
      message.success("Código reenviado correctamente");
      setTimer(45);
      setCanResend(false);
    } catch {
      message.error("No se pudo reenviar el código");
    }
  };

  const handleForgotPasswordSubmit = async (client_id) => {
    setEmail(client_id);
    clearErrorsAndSetLoading();
    try {
      await resetPassword({ email: client_id });
      message.success(
        "Solicitud de restablecimiento de contraseña enviada con éxito."
      );
      setCurrentForm("newPassword");
    } catch (error) {
      setError(error instanceof CustomError ? error.message : "General Error");
    } finally {
      setLoading(false);
    }
  };

  const handleNewPasswordSubmit = async ({ client_id, code, password }) => {
    clearErrorsAndSetLoading();
    try {
      await finalizeResetPassword({
        email: client_id,
        code,
        new_password: password,
      });
      message.success("Contraseña restablecida correctamente.");
      setCurrentForm("login");
    } catch (error) {
      setError(error instanceof CustomError ? error.message : "General Error");
    } finally {
      setLoading(false);
    }
  };

  const rulesEmail = [
    {
      validator: async (_, value) => {
        const emailRegex = /^[A-Za-z0-9._%+-]+@[A-Za-z0-9.-]+\.[A-Za-z]{2,}$/i;
        if (!emailRegex.test(value) && value.length < 6) {
          throw new Error("Ingrese un usuario o email válido");
        }
      },
    },
  ];

  const handleFinish = (values) => {
    if (currentForm === "login") handleSubmit(values);
    if (currentForm === "newPassword") handleNewPasswordSubmit(values);
  };

  const renderLoginForm = () => (
    <Form
      name="login"
      className="login-form"
      onFinish={handleFinish}
      autoComplete="off"
    >
      <div className="login-field">
        <label className="login-label">USUARIO O EMAIL</label>
        <Form.Item name="client_id" rules={rulesEmail} noStyle>
          <Input
            prefix={<MailOutlined className="login-input-icon" />}
            placeholder="nombre@egreen.com"
            className="login-input"
            bordered={false}
          />
        </Form.Item>
        <div className="login-input-line" />
      </div>

      <div className="login-field">
        <div className="login-label-row">
          <label className="login-label">CONTRASEÑA</label>
          <Link
            className="login-forgot"
            onClick={() => {
              setError("");
              setCurrentForm("forgotPassword");
            }}
          >
            Olvidé mi contraseña
          </Link>
        </div>
        <Form.Item
          name="password"
          rules={[{ required: true, message: "Por favor ingrese su password" }]}
          noStyle
        >
          <Input.Password
            prefix={<LockOutlined className="login-input-icon" />}
            className="login-input login-input-password"
            bordered={false}
          />
        </Form.Item>
        <div className="login-input-line" />
      </div>

      {error && (
        <div className="login-error">
          <WarningOutlined /> {error}
        </div>
      )}

      <Form.Item noStyle>
        {loading ? (
          <div className="login-spin">
            <Spin />
          </div>
        ) : (
          <button type="submit" className="login-btn">
            INICIAR SESIÓN
          </button>
        )}
      </Form.Item>

      <div className="login-register">
        ¿Aún no tienes cuenta?{" "}
        <span className="login-register-link">Regístrate ahora</span>
      </div>
    </Form>
  );

  const renderForgotForm = () => (
    <Form
      name="forgotPassword"
      className="login-form"
      onFinish={handleFinish}
      initialValues={{ client_id: email }}
      autoComplete="off"
    >
      <div className="login-field">
        <label className="login-label">USUARIO O EMAIL</label>
        <Form.Item name="client_id" rules={rulesEmail} noStyle>
          <Input
            prefix={<MailOutlined className="login-input-icon" />}
            className="login-input"
            bordered={false}
          />
        </Form.Item>
        <div className="login-input-line" />
      </div>

      {error && (
        <div className="login-error">
          <WarningOutlined /> {error}
        </div>
      )}

      <div className="login-form-actions">
        {loading ? (
          <div className="login-spin">
            <Spin />
          </div>
        ) : (
          <>
            <button type="submit" className="login-btn">
              ENVIAR
            </button>
            <button
              type="button"
              className="login-btn login-btn-secondary"
              onClick={() => {
                setCurrentForm("login");
                setError("");
              }}
            >
              REGRESAR
            </button>
          </>
        )}
      </div>
    </Form>
  );

  const renderNewPasswordForm = () => (
    <Form
      name="newPassword"
      className="login-form"
      onFinish={handleFinish}
      initialValues={{ client_id: email }}
      autoComplete="off"
    >
      {[
        { label: "EMAIL", name: "client_id", rules: rulesEmail },
        {
          label: "CÓDIGO",
          name: "code",
          rules: [{ required: true, message: "Por favor ingrese el código" }],
        },
        {
          label: "CONTRASEÑA",
          name: "password",
          rules: [{ required: true, message: "Ingrese su nueva contraseña" }],
          isPassword: true,
        },
        {
          label: "CONFIRMAR CONTRASEÑA",
          name: "password_confirm",
          isPassword: true,
          rules: [
            { required: true, message: "Confirme su nueva contraseña" },
            ({ getFieldValue }) => ({
              validator(_, value) {
                if (!value || getFieldValue("password") === value)
                  return Promise.resolve();
                return Promise.reject(
                  new Error("Las contraseñas no coinciden")
                );
              },
            }),
          ],
        },
      ].map((item) => (
        <div className="login-field" key={item.name}>
          <label className="login-label">{item.label}</label>
          <Form.Item name={item.name} rules={item.rules} noStyle>
            {item.isPassword ? (
              <Input.Password
                prefix={<LockOutlined className="login-input-icon" />}
                className="login-input"
                bordered={false}
              />
            ) : (
              <Input
                prefix={<MailOutlined className="login-input-icon" />}
                className="login-input"
                bordered={false}
              />
            )}
          </Form.Item>
          <div className="login-input-line" />
        </div>
      ))}

      {error && (
        <div className="login-error">
          <WarningOutlined /> {error}
        </div>
      )}

      <div className="login-form-actions">
        {loading ? (
          <div className="login-spin">
            <Spin />
          </div>
        ) : (
          <>
            <button type="submit" className="login-btn">
              RESTABLECER CONTRASEÑA
            </button>
            <button
              type="button"
              className="login-btn login-btn-secondary"
              onClick={() => {
                setCurrentForm("login");
                setError("");
              }}
            >
              REGRESAR
            </button>
          </>
        )}
      </div>
    </Form>
  );

  const renderMfaForm = () => (
    <Form name="mfa" className="login-form" onFinish={handleVerify}>
      <MFA />
      <div className="login-spin" style={{ marginTop: 20 }}>
        {loading ? (
          <Spin />
        ) : (
          <button type="submit" className="login-btn">
            VERIFICAR
          </button>
        )}
      </div>
      <div
        className={`login-resend ${canResend ? "login-resend-active" : ""}`}
        onClick={canResend ? handleResend : undefined}
      >
        {canResend ? "Reenviar código" : `Reenviar código en ${timer}s`}
      </div>
    </Form>
  );

  const formTitles = {
    login: {
      title: "Bienvenido de nuevo",
      subtitle: "Introduce tus credenciales para gestionar tus créditos.",
    },
    forgotPassword: {
      title: "Recuperar contraseña",
      subtitle: "Ingresa tu email y te enviaremos un código de recuperación.",
    },
    newPassword: {
      title: "Nueva contraseña",
      subtitle: "Ingresa el código recibido y establece tu nueva contraseña.",
    },
    mfa: {
      title: "Verificación MFA",
      subtitle: "Ingresa el código de verificación enviado a tu dispositivo.",
    },
  };

  const { title, subtitle } = formTitles[currentForm];

  return (
    <div className="login-page">
      <div className="login-card">
        <div className="login-left">
          <div className="login-brand">
            <img src="/egreen_logo.png" alt="logo" width="25" height="25" />
            <span className="login-brand-name">Carbon Tech Editorial</span>
          </div>
          <div className="login-left-body">
            <h1 className="login-headline">Transparencia Atmosférica.</h1>
            <p className="login-description">
              Accede a la plataforma líder en gestión de activos de carbono con
              precisión quirúrgica.
            </p>
          </div>
          <div />
        </div>

        <div className="login-right">
          <div className="login-right-inner">
            <h2 className="login-title">{title}</h2>
            <p className="login-subtitle">{subtitle}</p>

            {currentForm === "login" && renderLoginForm()}
            {currentForm === "forgotPassword" && renderForgotForm()}
            {currentForm === "newPassword" && renderNewPasswordForm()}
            {currentForm === "mfa" && renderMfaForm()}
          </div>
        </div>
      </div>

      <footer className="login-footer">
        <div className="login-footer-links">
          <span className="login-footer-link">PRIVACY POLICY</span>
          <span className="login-footer-link">TERMS OF SERVICE</span>
          <span className="login-footer-link">SECURITY</span>
        </div>
      </footer>
    </div>
  );
};

export default Login;
